import { ConnectButton, darkTheme, getDefaultConfig, RainbowKitProvider } from "@rainbow-me/rainbowkit";
import {
  coinbaseWallet, injectedWallet, metaMaskWallet, phantomWallet,
  rabbyWallet, rainbowWallet, walletConnectWallet,
} from "@rainbow-me/rainbowkit/wallets";
import { browserRpcUrl } from "@squeek/module-contracts/rpc-providers";
import { Button } from "@squeek/ui";
import type { ReactNode } from "react";
import { http } from "viem";
import { useAccount, useConfig, useDisconnect, useSignMessage, WagmiProvider } from "wagmi";
import { mainnet, sepolia } from "wagmi/chains";
import "@rainbow-me/rainbowkit/styles.css";

const projectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID?.trim() || null;
export const walletConfig = getDefaultConfig({
  appName: "Squeekchain Community Admin",
  appDescription: "Restricted community identity administration.",
  chains: [sepolia, mainnet],
  projectId: projectId ?? "walletconnect-disabled",
  transports: { [sepolia.id]: http(browserRpcUrl(sepolia.id)), [mainnet.id]: http(browserRpcUrl(mainnet.id)) },
  wallets: [{
    groupName: "Wallets",
    wallets: [rainbowWallet, metaMaskWallet, coinbaseWallet, phantomWallet, rabbyWallet,
      ...(projectId ? [walletConnectWallet] : []), injectedWallet],
  }],
});

const theme = darkTheme({
  accentColor: "var(--sqk-active)",
  accentColorForeground: "var(--sqk-on-active)",
  borderRadius: "small",
});

export function AdminWalletProvider({ children }: { children: ReactNode }) {
  return <WagmiProvider config={walletConfig}>{children}</WagmiProvider>;
}

export function AdminWalletUi({ children }: { children: ReactNode }) {
  return <RainbowKitProvider modalSize="compact" theme={theme}>{children}</RainbowKitProvider>;
}

/** Wallet selection is always explicit; session restoration never opens a wallet prompt. */
export function AdminWalletControl() {
  return <ConnectButton.Custom>{({ mounted, account, chain, openAccountModal, openConnectModal, openChainModal }) => (
    <Button disabled={!mounted} onClick={!account ? openConnectModal : chain?.unsupported ? openChainModal : openAccountModal}>
      {!account ? "Connect wallet" : chain?.unsupported ? "Choose network" : account.displayName}
    </Button>
  )}</ConnectButton.Custom>;
}

/**
 * Signs with the live connector instance. A connection restored from storage can still point at a
 * serialized connector without methods (wagmi then throws "connector.getChainId is not a function");
 * in that case the stale connection is dropped and the user is asked to reconnect.
 */
export function useWalletSigner(): (address: `0x${string}`, message: string) => Promise<`0x${string}`> {
  const config = useConfig();
  const account = useAccount();
  const { disconnectAsync } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  return async (address, message) => {
    const current = account.connector;
    const connector = config.connectors.find((item) => item.uid === current?.uid)
      ?? config.connectors.find((item) => item.id === current?.id);
    if (!connector || typeof connector.getChainId !== "function") {
      await disconnectAsync().catch(() => undefined);
      throw new Error("Your wallet connection went stale. Connect your wallet again, then sign in.");
    }
    try {
      return await signMessageAsync({ account: address, message, connector });
    } catch (error) {
      if (error instanceof Error && /getChainId is not a function|Connector not connected/i.test(error.message)) {
        await disconnectAsync().catch(() => undefined);
        throw new Error("Your wallet connection went stale. Connect your wallet again, then sign in.");
      }
      throw error;
    }
  };
}
